import {ReceiptForwardMode} from '@/models/enum/ReceiptForwardMode';
import MerchantDTO from '@/models/MerchantDTO';
import MerchantFormDTO from '@/models/form/MerchantFormDTO';

export const RECEIPT_FORWARD_FIELD_CATALOG = [
  { key: 'public_id', label: 'receipt_forward_field_public_id' },
  { key: 'internal_id', label: 'receipt_forward_field_internal_id' },
  { key: 'external_id', label: 'receipt_forward_field_external_id' },
  { key: 'payment_method', label: 'receipt_forward_field_payment_method' },
  { key: 'requisites', label: 'receipt_forward_field_requisites' },
  { key: 'amounts', label: 'receipt_forward_field_amounts' },
  { key: 'device', label: 'receipt_forward_field_device' },
] as const;

export const DEFAULT_RECEIPT_FORWARD_FIELDS = [
  'public_id',
  'payment_method',
  'requisites',
  'amounts',
];

export const RECEIPT_FORWARD_ACTION_CATALOG = [
  { key: 'success', label: 'receipt_forward_action_success' },
  { key: 'statement_required', label: 'receipt_forward_action_statement_required' },
  { key: 'video_required', label: 'receipt_forward_action_video_required' },
  { key: 'pdf_required', label: 'receipt_forward_action_pdf_required' },
  { key: 'client_statistics', label: 'receipt_forward_action_client_statistics' },
  { key: 'wrong_requisites', label: 'receipt_forward_action_wrong_requisites' },
  { key: 'wrong_amount', label: 'receipt_forward_action_wrong_amount' },
  { key: 'other', label: 'receipt_forward_action_other' },
  { key: 'check_receipt', label: 'receipt_forward_action_check_receipt' },
] as const;

export const DEFAULT_RECEIPT_FORWARD_ACTIONS = RECEIPT_FORWARD_ACTION_CATALOG.map((item) => item.key);

export interface MerchantReceiptForwardModalSettings {
  tgReceiptForwardGroupId: string | null;
  tgReceiptActionsGroupId: string | null;
  receiptForwardMode: ReceiptForwardMode;
  receiptForwardComment: string | null;
  receiptForwardTargetChatIds: string[];
  receiptForwardFields: string[];
  receiptForwardActions: string[];
}

export interface MerchantReceiptForwardSettings extends MerchantReceiptForwardModalSettings {
  siteUploadForwardEnabled: boolean;
}

export function normalizeReceiptForwardModalSettings(
  source: Partial<MerchantFormDTO | MerchantDTO>,
): MerchantReceiptForwardModalSettings {
  return {
    tgReceiptForwardGroupId: source.tgReceiptForwardGroupId ?? null,
    tgReceiptActionsGroupId: source.tgReceiptActionsGroupId ?? null,
    receiptForwardMode: source.receiptForwardMode ?? ReceiptForwardMode.FULL,
    receiptForwardComment: source.receiptForwardComment ?? null,
    receiptForwardTargetChatIds: [...(source.receiptForwardTargetChatIds ?? [])],
    receiptForwardFields: [...(source.receiptForwardFields ?? DEFAULT_RECEIPT_FORWARD_FIELDS)],
    receiptForwardActions: [...(source.receiptForwardActions ?? DEFAULT_RECEIPT_FORWARD_ACTIONS)],
  };
}

export function normalizeReceiptForwardSettings(
  source: Partial<MerchantFormDTO | MerchantDTO>,
): MerchantReceiptForwardSettings {
  return {
    ...normalizeReceiptForwardModalSettings(source),
    siteUploadForwardEnabled: source.siteUploadForwardEnabled ?? false,
  };
}

export function serializeReceiptForwardComment(comment: string | null | undefined): string | null {
  const trimmed = (comment ?? '').trim();
  return trimmed === '' ? '' : comment ?? '';
}

export function serializeReceiptForwardTargetChatIds(chatIds: string[] | undefined): string[] {
  return (chatIds ?? []).filter(chatId => chatId.trim() !== '');
}

export function receiptForwardSettingsFromSubmitted(
  submitted: Partial<MerchantFormDTO>,
): MerchantReceiptForwardSettings {
  return {
    ...normalizeReceiptForwardSettings(submitted),
    receiptForwardComment: serializeReceiptForwardComment(submitted.receiptForwardComment),
    receiptForwardTargetChatIds: serializeReceiptForwardTargetChatIds(submitted.receiptForwardTargetChatIds),
  };
}

export function applyReceiptForwardSettingsToMerchant(
  merchant: MerchantDTO,
  settings: MerchantReceiptForwardSettings,
): void {
  merchant.tgReceiptForwardGroupId = settings.tgReceiptForwardGroupId;
  merchant.tgReceiptActionsGroupId = settings.tgReceiptActionsGroupId;
  merchant.receiptForwardMode = settings.receiptForwardMode;
  merchant.receiptForwardComment = settings.receiptForwardComment;
  merchant.receiptForwardTargetChatIds = [...settings.receiptForwardTargetChatIds];
  merchant.receiptForwardFields = [...settings.receiptForwardFields];
  merchant.receiptForwardActions = [...settings.receiptForwardActions];
  merchant.siteUploadForwardEnabled = settings.siteUploadForwardEnabled;
}

export function applyReceiptForwardModalSettingsToForm(
  form: MerchantFormDTO,
  settings: MerchantReceiptForwardModalSettings,
): void {
  form.tgReceiptForwardGroupId = settings.tgReceiptForwardGroupId;
  form.tgReceiptActionsGroupId = settings.tgReceiptActionsGroupId;
  form.receiptForwardMode = settings.receiptForwardMode;
  form.receiptForwardComment = settings.receiptForwardComment;
  form.receiptForwardTargetChatIds = [...settings.receiptForwardTargetChatIds];
  form.receiptForwardFields = [...settings.receiptForwardFields];
  form.receiptForwardActions = [...settings.receiptForwardActions];
}

export function applyReceiptForwardSettingsToForm(
  form: MerchantFormDTO,
  settings: MerchantReceiptForwardSettings,
): void {
  applyReceiptForwardModalSettingsToForm(form, settings);
  form.siteUploadForwardEnabled = settings.siteUploadForwardEnabled;
}

export const receiptForwardFieldPrefixes = [
  'tgReceiptForwardGroupId',
  'tgReceiptActionsGroupId',
  'receiptForwardMode',
  'receiptForwardComment',
  'receiptForwardTargetChatIds',
  'receiptForwardFields',
  'receiptForwardActions',
] as const;
